import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { GraduationCap, ArrowLeft } from "@phosphor-icons/react";
import { Link, useSearchParams } from "react-router-dom";
import { SCHOOLS } from "../components/site/siteData";

export default function Login() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const school = SCHOOLS.find((s) => s.slug === params.get("school"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const error = await login(email, password);
    if (error) setErr(error);
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-zinc-900 text-white relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-indigo-500 grid place-items-center">
            <GraduationCap size={22} weight="bold" />
          </div>
          <div className="font-display text-xl font-bold tracking-tight">Scholaris</div>
        </div>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest text-indigo-300 mb-3">
            Academic Year 2026 — 2027
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black leading-tight max-w-md">
            One portal for every day of school life.
          </h1>
          <p className="mt-4 text-zinc-300 max-w-md">
            Attendance, lesson plans, tasks, exams, and parent conversations — all
            moving in sync from a single dashboard.
          </p>
        </div>
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/5147366/pexels-photo-5147366.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/70 to-transparent" />
        <div className="text-sm text-zinc-400 relative z-10">
          Built for principals who want signal, not noise.
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-zinc-50">
        <Card className="w-full max-w-md p-8 border-zinc-200">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-indigo-700 mb-4 transition-colors">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="mb-6">
            {school && <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-1" data-testid="login-school-name">{school.name}</div>}
            <h2 className="font-display text-2xl font-bold">Sign in</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Use your school credentials to continue.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required
                data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-button"
              className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2"
              >
                {err}
              </div>
            )}
          </form>

        </Card>
      </div>
    </div>
  );
}
